import React, { Component, createRef } from "react";
import styled, { css } from "styled-components";
import MediumEditor from "medium-editor";
import "medium-editor/dist/css/medium-editor.min.css";
import "medium-editor/dist/css/themes/default.min.css";
import { marked } from "marked";
import TurndownService from "turndown";
import { Divider } from "js/Components/Divider";
import { t } from "js/i18n";

const MEDIUM_EDITOR_TOOLBAR_BUTTONS = [
    "bold",
    "italic",
    "underline",
    "strikethrough",
    {
        name: "unorderedlist",
        classList: ["unorderedlist-btn"]
    },
    {
        name: "orderedlist",
        classList: ["orderedlist-btn"]
    },
    "indent",
    "outdent"
];

const MEDIUM_EDITOR_STATIC_OPTIONS = {
    toolbar: {
        buttons: MEDIUM_EDITOR_TOOLBAR_BUTTONS,
        static: true,
        sticky: true,
        updateOnEmptySelection: true
    },
    paste: {
        cleanPastedHTML: true
    },
    placeholder: false
};

const Container = styled.div`
    font-size: 14px;
    width: 100%;
    height: 100%;

    ${({ disabled }) => disabled && css`
        opacity: 0.5;
        cursor: not-allowed;
    `}

    ul, ol {
        margin: 6px 0;
    }

    h1, h2, h3, h4 {
        margin: 0;
        margin-bottom: 10px;
        font-size: 16px;
    }

    h4 {
        font-size: 14px;
    }

    table td {
        border: solid 1px #ccc;
        padding: 4px 10px;
    }

    p {
        margin-bottom: 10px;
        line-height: 1.4;
    }
`;

const ToolbarContainer = styled.div`
    position: sticky;
    top: 0;
    z-index: 2;
    background: ${({ $toolbarTheme }) => ($toolbarTheme === "dark" ? "#222222" : "#fff")};

    .medium-editor-toolbar {
        position: relative;
        border-radius: 0 !important;
        box-shadow: none !important;
        background: none !important;
        border: none !important;
        display: block !important;
        visibility: visible !important;
    }

    .medium-editor-toolbar li button {
        min-width: 30px !important;
        height: 32px !important;
        padding: 0 !important;
        border: none !important;
        background: none !important;
        box-shadow: none !important;
        color: ${({ $toolbarTheme }) => ($toolbarTheme === "dark" ? "#c9d1d9" : "#777")} !important;

        &:hover {
            color: ${({ $toolbarTheme }) => ($toolbarTheme === "dark" ? "#58a6ff" : "#11a9e2")} !important;
        }
    }

    .medium-editor-toolbar li button.medium-editor-button-active {
        color: ${({ $toolbarTheme }) => ($toolbarTheme === "dark" ? "#58a6ff" : "#11a9e2")} !important;
    }

    .medium-editor-action {
        display: flex !important;
        align-items: center;
        justify-content: center;

        i {
            font-size: 18px;
        }
    }

    ${({ $showToolbarOnFocusOnly }) => $showToolbarOnFocusOnly && css`
        display: none;

        ${Container}:focus-within & {
            display: block;
        }
    `}
`;

const ToolbarDivider = styled(Divider)`
    ${({ $showToolbarOnFocusOnly }) => $showToolbarOnFocusOnly && css`
        display: none;

        ${Container}:focus-within & {
            display: block;
        }
    `}
`;

const EditorContainer = styled.div`
    outline: none;
    height: auto;
    min-height: 80px;
    padding: 8px 0;
    font-size: 14px;
    cursor: text;
    overflow: visible;
    ${({ $disabled }) => $disabled && css`
        pointer-events: none;
        cursor: not-allowed;
    `}

    &:empty::before {
        content: attr(data-placeholder);
        color: #aaa;
        font-style: italic;
        pointer-events: none;
    }
`;

export class AiSlideContentEditor extends Component {
    constructor(props) {
        super(props);

        this.html = this.normalizeText(props.text);
        this.isEditing = false;
        this.toolbarContainerRef = createRef();
        this.editorContainerRef = createRef();
        this.mediumEditor = null;
    }

    normalizeText(text) {
        // strip outer quotes from text
        text = text?.replace(/^"+|"+$/g, "");

        let html = marked.parse(text ?? "", { breaks: true });
        // strip trailing <br>
        html = html.replace(/<br>$/g, "");

        return html;
    }

    componentDidMount() {
        this.setupMediumEditor();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.text !== this.props.text && !this.isEditing) {
            this.html = this.normalizeText(this.props.text);
            if (this.mediumEditor) {
                this.mediumEditor.setContent(this.html, 0);
            }
        }

        if (prevProps.disabled !== this.props.disabled && this.editorContainerRef.current) {
            this.editorContainerRef.current.setAttribute("contenteditable", this.props.disabled ? "false" : "true");
            if (this.props.disabled) {
                this.editorContainerRef.current.blur();
            }
        }
    }

    componentWillUnmount() {
        this.editorContainerRef.current?.removeEventListener("keydown", this.handleEditorKeyDown);
        this.mediumEditor?.destroy();
    }

    getMediumEditorOptions() {
        return {
            ...MEDIUM_EDITOR_STATIC_OPTIONS,
            toolbar: {
                ...MEDIUM_EDITOR_STATIC_OPTIONS.toolbar,
                relativeContainer: this.toolbarContainerRef.current
            }
        };
    }

    setupMediumEditor() {
        this.mediumEditor = new MediumEditor(this.editorContainerRef.current, this.getMediumEditorOptions());
        this.editorContainerRef.current?.setAttribute("contenteditable", this.props.disabled ? "false" : "true");

        this.mediumEditor.setContent(this.html, 0);

        this.mediumEditor.subscribe("editableInput", () => {
            this.mergeAdjacentLists(this.editorContainerRef.current);
            const content = this.mediumEditor.getContent(0);
            this.html = content;
            this.props.onChange(content);
        });

        this.mediumEditor.subscribe("focus", () => {
            this.isEditing = true;
        });

        this.mediumEditor.subscribe("blur", () => {
            this.isEditing = false;
        });

        this.editorContainerRef.current?.addEventListener("keydown", this.handleEditorKeyDown);

        // Focus the editor when a toolbar button is clicked while editor is blurred,
        // so the formatting action applies correctly
        this.toolbarContainerRef.current?.addEventListener("mousedown", e => {
            if (this.props.disabled) {
                return;
            }
            if (!this.editorContainerRef.current.contains(document.activeElement)) {
                this.editorContainerRef.current.focus();
            }
        });

        // Replace list button icons with material icons within this instance's toolbar
        const toolbar = this.toolbarContainerRef.current;
        if (toolbar) {
            const ulBtn = toolbar.querySelector(".unorderedlist-btn");
            const olBtn = toolbar.querySelector(".orderedlist-btn");
            if (ulBtn) ulBtn.innerHTML = '<i class="micon">format_list_bulleted</i>';
            if (olBtn) olBtn.innerHTML = '<i class="micon">format_list_numbered</i>';
        }
    }

    mergeAdjacentLists(root) {
        if (!root) {
            return;
        }

        const parents = [root, ...root.querySelectorAll("*")];

        for (const parent of parents) {
            let current = parent.firstElementChild;

            while (current) {
                const currentTag = current.tagName?.toLowerCase();
                if (currentTag !== "ul" && currentTag !== "ol") {
                    current = current.nextElementSibling;
                    continue;
                }

                let next = current.nextElementSibling;
                while (next && next.tagName?.toLowerCase() === currentTag) {
                    while (next.firstChild) {
                        current.appendChild(next.firstChild);
                    }

                    const merged = next;
                    next = next.nextElementSibling;
                    merged.remove();
                }

                current = next;
            }
        }
    }

    handleEditorKeyDown = event => {
        if (this.props.disabled) {
            event.preventDefault();
            return;
        }

        const selection = window.getSelection?.();
        if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) {
            return;
        }

        const range = selection.getRangeAt(0);
        const startNode = range.startContainer;
        const startElement = startNode?.nodeType === 1 ? startNode : startNode?.parentElement;
        const listItem = startElement?.closest?.("li");

        const hasModKey = event.metaKey || event.ctrlKey;
        if (hasModKey) {
            const key = event.key?.toLowerCase?.();
            if (key === "b") {
                event.preventDefault();
                document.execCommand("bold", false, null);
                this.mediumEditor?.checkContentChanged?.();
                return;
            }
            if (key === "i") {
                event.preventDefault();
                document.execCommand("italic", false, null);
                this.mediumEditor?.checkContentChanged?.();
                return;
            }
        }

        if (event.key === "Backspace" && listItem && this.isCaretAtElementBoundary(selection, listItem, "start")) {
            event.preventDefault();
            document.execCommand("outdent", false, null);
            this.mediumEditor?.checkContentChanged?.();
            return;
        }

        if (event.key === " " && !listItem) {
            const editorRoot = this.editorContainerRef.current;
            const blockElement = startElement?.closest?.("p, div, h1, h2, h3, h4");
            if (!blockElement || blockElement === editorRoot) {
                return;
            }

            const beforeRange = range.cloneRange();
            beforeRange.selectNodeContents(blockElement);
            beforeRange.setEnd(range.startContainer, range.startOffset);
            const prefix = beforeRange.toString().trim();

            let listCommand = null;
            if (prefix === "-" && this.isCaretAtElementBoundary(selection, blockElement, "end")) {
                listCommand = "insertUnorderedList";
            } else if (/^\d+\.$/.test(prefix) && this.isCaretAtElementBoundary(selection, blockElement, "end")) {
                listCommand = "insertOrderedList";
            }

            if (!listCommand) {
                return;
            }

            event.preventDefault();
            // Select all content in the block and delete via execCommand to keep the element in a valid DOM state.
            // Setting textContent = "" leaves the element with no child nodes, which can cause insertUnorderedList to operate on the wrong (previous) block.
            const deleteRange = document.createRange();
            deleteRange.selectNodeContents(blockElement);
            selection.removeAllRanges();
            selection.addRange(deleteRange);
            document.execCommand("delete", false, null);
            document.execCommand(listCommand, false, null);
            this.mediumEditor?.checkContentChanged?.();
        }
    }

    isCaretAtElementBoundary(selection, element, boundary) {
        const range = selection.getRangeAt(0);
        const boundaryRange = range.cloneRange();
        boundaryRange.selectNodeContents(element);

        if (boundary === "start") {
            boundaryRange.setEnd(range.startContainer, range.startOffset);
        } else {
            boundaryRange.setStart(range.startContainer, range.startOffset);
        }

        const content = boundaryRange.toString().replace(/\u200B/g, "").trim();
        return content.length === 0;
    }

    placeCaretAtStartOfElement(element) {
        const selection = window.getSelection?.();
        if (!selection) {
            return;
        }

        const range = document.createRange();
        range.selectNodeContents(element);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
    }

    getMarkdown() {
        const html = this.normalizeNestedLists(this.html);
        const markdown = new TurndownService().turndown(html);
        return markdown;
    }

    normalizeNestedLists(html) {
        const container = document.createElement("div");
        container.innerHTML = html;

        const normalizeList = list => {
            let prevListItem = null;

            for (const child of Array.from(list.children)) {
                const tagName = child.tagName?.toLowerCase();

                if (tagName === "li") {
                    prevListItem = child;
                    for (const nested of Array.from(child.children)) {
                        const nestedTag = nested.tagName?.toLowerCase();
                        if (nestedTag === "ul" || nestedTag === "ol") {
                            normalizeList(nested);
                        }
                    }
                }

                if ((tagName === "ul" || tagName === "ol") && prevListItem) {
                    // execCommand("indent") can create sibling nested lists.
                    // Reattach those lists under the previous <li> to preserve hierarchy.
                    prevListItem.appendChild(child);
                    normalizeList(child);
                }
            }
        };

        container.querySelectorAll("ul, ol").forEach(normalizeList);
        return container.innerHTML;
    }

    focus() {
        if (this.editorContainerRef.current) {
            this.editorContainerRef.current.focus();
        }
    }

    render() {
        const {
            showToolbarOnFocusOnly = false,
            toolbarTheme = "light",
            disabled = false
        } = this.props;

        return (
            <Container disabled={disabled}>
                <ToolbarContainer
                    ref={this.toolbarContainerRef}
                    $showToolbarOnFocusOnly={showToolbarOnFocusOnly}
                    $toolbarTheme={toolbarTheme}
                />
                <ToolbarDivider $showToolbarOnFocusOnly={showToolbarOnFocusOnly} />
                <EditorContainer
                    ref={this.editorContainerRef}
                    $disabled={disabled}
                    data-placeholder={t("Enter a description of the slide...")}
                />
            </Container>
        );
    }
}
